import { type StateObservable } from 'redux-observable';
import { concat, type Observable, of, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';

import { type Actions as RootActions } from '../actions';
import { getHighAccountUsage } from '../gonfalon';
import { type AppState } from '../reducer';
import { type RequestError } from '../shared';
import { safeWithLatestFromState } from '../utils/safeWithLatestFrom';

import { Actions, ActionTypes } from './actions';
import {
  cancelSubscription as cancelSubscriptionApi,
  changeAccountSubscriptionDates,
  changeEnterpriseTrialCampaignDates,
  changeGuardianTrialCampaignDates,
  fetchEapSegments,
  getAccount,
  getAccountAuditLogEntries,
  getAccountAuditLogEntryDetail,
  getAccountCustomRoles,
  getAccountMembers,
  getAccountMemberSessions,
  getAccounts,
  getAccountSubscription,
  getAccountSubscriptionUsage,
  getLegacyAccountSubscription,
  patchLegacyAccountSubscription,
  postProfileMfaDisable,
  putSubscription,
  resendMemberInvitation,
  searchMembers,
} from './api';
import { type Account, type LegacySubscription, type Subscription } from './model';

export const accountsFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_ACCOUNTS }> =>
        action.type === ActionTypes.FETCH_ACCOUNTS,
    ),
    switchMap(({ q, unpaid, overage }) =>
      getAccounts({ q, unpaid, overage }).pipe(
        map((response) => Actions.receiveAccounts(q, response)),
        catchError((error) => of(Actions.fetchAccountsFailed(q, error))),
      ),
    ),
  );

export const accountFetch = (action$: Observable<RootActions>, state$: StateObservable<AppState>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_ACCOUNT }> =>
        action.type === ActionTypes.FETCH_ACCOUNT,
    ),
    safeWithLatestFromState(state$),
    mergeMap(([{ accountId }, state]) =>
      getAccount(accountId).pipe(
        mergeMap((response) => {
          const account = response.entities.accounts[accountId];
          return concat(
            of(Actions.fetchLegacySubscription(account)),
            of(Actions.fetchSubscription(account)),
            of(Actions.fetchSubscriptionUsage(account)),
            of(Actions.fetchMemberSessions(account, state.settings.enableMemberSessions)),
            of(Actions.fetchCustomRoles(account)),
            of(Actions.receiveAccount(accountId, response)),
          );
        }),
        catchError((error) => of(Actions.fetchAccountFailed(accountId, error))),
      ),
    ),
  );

export const accountLegacySubscriptionFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_LEGACY_SUBSCRIPTION }> =>
        action.type === ActionTypes.FETCH_LEGACY_SUBSCRIPTION,
    ),
    mergeMap(({ account }) =>
      getLegacyAccountSubscription(account).pipe(
        map((response) => Actions.receiveLegacySubscription(account, response)),
        catchError((error) => of(Actions.fetchLegacySubscriptionFailed(account, error))),
      ),
    ),
  );

export const accountSubscriptionFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_SUBSCRIPTION }> =>
        action.type === ActionTypes.FETCH_SUBSCRIPTION,
    ),
    mergeMap(({ account }) =>
      getAccountSubscription(account).pipe(
        map((response) => Actions.receiveSubscription(account, response)),
        catchError((error) => of(Actions.fetchSubscriptionFailed(account, error))),
      ),
    ),
  );

export const accountSubscriptionUsageFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_SUBSCRIPTION_USAGE }> =>
        action.type === ActionTypes.FETCH_SUBSCRIPTION_USAGE,
    ),
    mergeMap(({ account }) =>
      getAccountSubscriptionUsage(account).pipe(
        map((response) => Actions.receiveSubscriptionUsage(account, response)),
        catchError((error) => of(Actions.fetchSubscriptionUsageFailed(account, error))),
      ),
    ),
  );

export const accountMembersFetch = (action$: Observable<RootActions>, state$: StateObservable<AppState>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_MEMBERS }> =>
        action.type === ActionTypes.FETCH_MEMBERS,
    ),
    safeWithLatestFromState(state$),
    mergeMap(([action, state]) => {
      const { account, url } = action;
      return getAccountMembers(account, state.settings.enablePaginationOfMembers, url).pipe(
        mergeMap((accountMembersResponse) =>
          concat(
            of(Actions.receiveMembers(account, accountMembersResponse)),
            of(Actions.receiveMembersPaginationData(account, accountMembersResponse)),
          ),
        ),
        catchError((error) => of(Actions.fetchMembersFailed(account, error))),
      );
    }),
  );

export const accountMembersForExportFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_MEMBERS_FOR_EXPORT }> =>
        action.type === ActionTypes.FETCH_MEMBERS_FOR_EXPORT,
    ),
    mergeMap(({ account }) =>
      getAccountMembers(account, false).pipe(
        map((response) => Actions.receiveMembersForExport(account, response)),
        catchError((error) => of(Actions.fetchMembersForExportFailed(account, error))),
      ),
    ),
  );

export const accountMemberSessionsFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_MEMBER_SESSIONS }> =>
        action.type === ActionTypes.FETCH_MEMBER_SESSIONS,
    ),
    mergeMap(({ account }) =>
      getAccountMemberSessions(account).pipe(
        map((response) => Actions.receiveMemberSessions(account, response)),
        catchError((error) => of(Actions.fetchMemberSessionsFailed(account, error))),
      ),
    ),
  );

export const accountCustomRolesFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_CUSTOM_ROLES }> =>
        action.type === ActionTypes.FETCH_CUSTOM_ROLES,
    ),
    mergeMap(({ account }) =>
      getAccountCustomRoles(account).pipe(
        map((response) => Actions.receiveCustomRoles(account, response)),
        catchError((error) => of(Actions.fetchCustomRolesFailed(account, error))),
      ),
    ),
  );

export const accountHighUsageFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE }> =>
        action.type === ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE,
    ),
    mergeMap(({ usageType, dates }) =>
      getHighAccountUsage(usageType, dates).pipe(
        map((response) => Actions.receiveHighAccountUsage(response)),
        catchError((error) => of(Actions.fetchHighAccountUsageFailed(error))),
      ),
    ),
  );

export const accountAuditLogEntriesFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_AUDIT_LOG_ENTRIES }> =>
        action.type === ActionTypes.FETCH_AUDIT_LOG_ENTRIES,
    ),
    mergeMap(({ account, nextUrl, query, spec, date }) =>
      getAccountAuditLogEntries({ account, nextUrl, query, spec, date }).pipe(
        map((response) => Actions.receiveAuditLogEntries(account, response)),
        catchError((error) => of(Actions.fetchAuditLogEntriesFailed(account, error))),
      ),
    ),
  );

export const accountAuditLogEntryDetailsFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL }> =>
        action.type === ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL,
    ),
    mergeMap(({ account, entryUrl }) =>
      getAccountAuditLogEntryDetail(account, entryUrl).pipe(
        map((response) => Actions.receiveAuditLogEntryDetail(account, response)),
        catchError((error) => of(Actions.fetchAuditLogEntryDetailFailed(account, error))),
      ),
    ),
  );

export const accountSearch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SEARCH_ACCOUNTS }> =>
        action.type === ActionTypes.SEARCH_ACCOUNTS,
    ),
    filter(({ q }) => !!q),
    switchMap(({ q }) =>
      timer(500).pipe(
        takeUntil(
          action$.pipe(
            filter(
              (action): action is Extract<RootActions, { type: ActionTypes.CLEAR_ACCOUNT_RESULTS }> =>
                action.type === ActionTypes.CLEAR_ACCOUNT_RESULTS,
            ),
          ),
        ),
        mergeMap(() => getAccounts({ q }).pipe(map((Response) => Actions.receiveSearchResults(q, Response)))),
      ),
    ),
  );

export const accountSearchClearance = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SEARCH_ACCOUNTS }> =>
        action.type === ActionTypes.SEARCH_ACCOUNTS,
    ),
    filter((action: ReturnType<typeof Actions.searchAccounts>) => action.q === ''),
    mergeMap(() => of(Actions.clearAccountResults())),
  );

export const eapSegmentsFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_EAP_SEGMENTS }> =>
        action.type === ActionTypes.FETCH_EAP_SEGMENTS,
    ),
    mergeMap(({ account }) =>
      fetchEapSegments(account).pipe(
        map((response) => Actions.receiveEapSegments(account, response)),
        catchError((error) => of(Actions.fetchEapSegmentsFailed(account, error))),
      ),
    ),
  );

const subscriptionDateUpdates =
  (
    initialAction: ActionTypes.UPDATE_TRIAL_END | ActionTypes.UPDATE_GRACE_END,
    updateDate: (subscription: Subscription, date: Date) => object,
    onSuccess: (account: Account, subscription: Subscription, updatedSubscription: Subscription) => Actions,
    onError: (account: Account, error: RequestError) => Actions,
  ) =>
  (action$: Observable<RootActions>) =>
    action$.pipe(
      filter((action): action is Extract<RootActions, { type: typeof initialAction }> => action.type === initialAction),
      mergeMap((action) => {
        const typedAction = action as Actions & { account: Account; subscription: Subscription; date: Date };
        const { account, subscription, date } = typedAction;
        const updatedDate = updateDate(subscription, date);
        return changeAccountSubscriptionDates(account, updatedDate).pipe(
          map((response) => onSuccess(account, subscription, response)),
          catchError((error: RequestError) => of(onError(account, error))),
        );
      }),
    );

export const subscriptionTrialEndUpdates = subscriptionDateUpdates(
  ActionTypes.UPDATE_TRIAL_END,
  (subscription, date) => ({
    trialStartDate: subscription.trialStartDate,
    trialEndDate: date.toISOString(),
  }),
  Actions.updateTrialEndSucceeded,
  Actions.updateTrialEndFailed,
);

export const subscriptionGraceEndUpdates = subscriptionDateUpdates(
  ActionTypes.UPDATE_GRACE_END,
  (subscription, date) => ({
    graceStartDate: subscription.graceStartDate,
    graceEndDate: date.toISOString(),
  }),
  Actions.updateGraceEndSucceeded,
  Actions.updateGraceEndFailed,
);

const legacySubscriptionDateUpdates =
  (
    initialAction: ActionTypes.UPDATE_LEGACY_TRIAL_END | ActionTypes.UPDATE_LEGACY_GRACE_END,
    updateSubscription: (subscription: LegacySubscription, date: Date) => LegacySubscription,
    onSuccess: (account: Account, subscription: LegacySubscription, updatedSubscription: LegacySubscription) => Actions,
    onError: (account: Account, error: RequestError) => Actions,
  ) =>
  (action$: Observable<RootActions>) =>
    action$.pipe(
      filter((action): action is Extract<RootActions, { type: typeof initialAction }> => action.type === initialAction),
      mergeMap((action) => {
        const typedAction = action as Actions & { account: Account; subscription: LegacySubscription; date: Date };
        const { account, subscription, date } = typedAction;
        const newSubscription = updateSubscription(subscription, date);

        return patchLegacyAccountSubscription(account, subscription, newSubscription).pipe(
          map((response) => onSuccess(account, subscription, response)),
          catchError((error: RequestError) => of(onError(account, error))),
        );
      }),
    );

export const legacySubscriptionTrialEndUpdates = legacySubscriptionDateUpdates(
  ActionTypes.UPDATE_LEGACY_TRIAL_END,
  (subscription, date) => ({
    ...subscription,
    trialEndDate: date.toISOString(),
  }),
  Actions.updateLegacyTrialEndSucceeded,
  Actions.updateLegacyTrialEndFailed,
);

export const legacySubscriptionGraceEndUpdates = legacySubscriptionDateUpdates(
  ActionTypes.UPDATE_LEGACY_GRACE_END,
  (subscription, date) => ({
    ...subscription,
    graceEndDate: date.toISOString(),
  }),
  Actions.updateLegacyGraceEndSucceeded,
  Actions.updateLegacyGraceEndFailed,
);

const enterpriseTrialCampaignDateUpdates =
  (
    initialAction: ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES,
    updateDate: (subscription: Subscription, date: Date) => { startTime: string; endTime: string },
    onSuccess: (account: Account, subscription: Subscription, updatedSubscription: Subscription) => Actions,
    onError: (account: Account, error: RequestError) => Actions,
  ) =>
  (action$: Observable<RootActions>) =>
    action$.pipe(
      filter((action): action is Extract<RootActions, { type: typeof initialAction }> => action.type === initialAction),
      mergeMap((action) => {
        const typedAction = action as Actions & { account: Account; subscription: Subscription; date: Date };
        const { account, subscription, date } = typedAction;
        const updatedDate = updateDate(subscription, date);
        return changeEnterpriseTrialCampaignDates(account, updatedDate).pipe(
          map((response) => onSuccess(account, subscription, response)),
          catchError((error: RequestError) => of(onError(account, error))),
        );
      }),
    );

export const enterpriseTrialCampaignUpdates = enterpriseTrialCampaignDateUpdates(
  ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES,
  (subscription, date) => ({
    startTime: subscription.campaigns.CustomerEnterpriseCampaign.startDate,
    endTime: date.toISOString(),
  }),
  Actions.updateEnterpriseCampaignDatesSucceeded,
  Actions.updateEnterpriseCampaignDatesFailed,
);

const guardianTrialCampaignDateUpdates =
  (
    initialAction: ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES,
    updateDate: (subscription: Subscription, date: Date) => { startTime: string; endTime: string },
    onSuccess: (account: Account, subscription: Subscription, updatedSubscription: Subscription) => Actions,
    onError: (account: Account, error: RequestError) => Actions,
  ) =>
  (action$: Observable<RootActions>) =>
    action$.pipe(
      filter((action): action is Extract<RootActions, { type: typeof initialAction }> => action.type === initialAction),
      mergeMap((action) => {
        const typedAction = action as Actions & { account: Account; subscription: Subscription; date: Date };
        const { account, subscription, date } = typedAction;
        const updatedDate = updateDate(subscription, date);
        return changeGuardianTrialCampaignDates(account, updatedDate).pipe(
          map((response) => onSuccess(account, subscription, response)),
          catchError((error: RequestError) => of(onError(account, error))),
        );
      }),
    );

export const guardianTrialCampaignUpdates = guardianTrialCampaignDateUpdates(
  ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES,
  (subscription, date) => ({
    startTime: subscription.campaigns.CustomerGuardianCampaign.startDate,
    endTime: date.toISOString(),
  }),
  Actions.updateGuardianCampaignDatesSucceeded,
  Actions.updateGuardianCampaignDatesFailed,
);

export const subscribe = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SUBSCRIBE }> =>
        action.type === ActionTypes.SUBSCRIBE,
    ),
    mergeMap(({ account, subscription, plan }) =>
      putSubscription(account, plan).pipe(
        map((updatedSubscription) => Actions.subscribeSucceeded(account, subscription, updatedSubscription)),
        catchError((error: RequestError) => of(Actions.subscribeFailed(account, plan, error))),
      ),
    ),
  );

export const cancelSubscription = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.CANCEL_SUBSCRIPTION }> =>
        action.type === ActionTypes.CANCEL_SUBSCRIPTION,
    ),
    mergeMap(
      (
        { account, endImmediately }, // Extract endImmediately from action
      ) =>
        cancelSubscriptionApi(account, endImmediately).pipe(
          map(() => Actions.cancelSubscriptionFulfilled(account)),
          catchError((error: RequestError) => of(Actions.cancelSubscriptionFailed(account, error))),
        ),
    ),
  );

export const disableMfa = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.DISABLE_MFA }> =>
        action.type === ActionTypes.DISABLE_MFA,
    ),
    mergeMap(({ account, member }) =>
      postProfileMfaDisable(account, member).pipe(
        map((response) => Actions.setMfaDisabledSuccess(account, response)),
        catchError((error: RequestError) => of(Actions.setMfaDisabledFailed(account, member, error))),
      ),
    ),
  );

export const accountMembersSearchEpic = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SEARCH_MEMBERS }> =>
        action.type === ActionTypes.SEARCH_MEMBERS,
    ),
    filter(
      (action: Actions) => 'query' in action && typeof action.query === 'string' && action.query.trim().length > 0,
    ),
    switchMap((action: Actions) => {
      const typedAction = action as Actions & { account: Account; query: string };
      const { account, query } = typedAction;
      return searchMembers(account, query.trim()).pipe(
        map((response) => Actions.searchMembersFulfilled(account, query, response)),
        catchError((error) => of(Actions.searchMembersFailed(account, error))),
      );
    }),
  );

export const resendMemberInvitationEpic = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.RESEND_MEMBER_INVITATION }> =>
        action.type === ActionTypes.RESEND_MEMBER_INVITATION,
    ),
    mergeMap(({ memberId, account }) =>
      resendMemberInvitation(account, memberId).pipe(
        map(() => Actions.resendMemberInvitationSuccess(memberId)),
        catchError((error: RequestError) => of(Actions.resendMemberInvitationFailed(memberId, error))),
      ),
    ),
  );
